import React, { Component } from "react";
import { Link } from "react-router-dom";
import { connect } from 'react-redux';
import { Button, Table } from 'react-bootstrap';

import { ContainerWrapper, Content } from '../../components/GridComponent/AdminLte.js';
import PageLoading from "../PageLoading";
import Wrapper from "../../components/Template/Wrapper";
import { Select, Text } from "./BroadcastFormSections";
import { Metric } from "./broadcastResponsesHelpers";
import RaiseDocumentModal from "./RaiseDocumentModal";
import {
  fetchBroadcastAssignments, raiseBroadcastAssignment, withdrawBroadcastAssignment, exportBroadcastAssignments,
  downloadBroadcastPdf, searchBroadcastEmployees,
} from '../../store/actions/broadcasts/broadcastActions';
import "./Broadcasts.css";

/**
 * Announcements -> Manage -> row (a per-person form) -> Documents (/app/announcements/:id/documents).
 * SPEC 23.4 / 23.8: pending / signed counts, a status filter and a search (emp_num / name), one
 * row per document (withdrawn ones are never listed by the server), Raise document, Withdraw on a
 * pending row, the PDF of any row (managers get pending ones watermarked), and the Excel export.
 *
 * The content is the server's GET /broadcasts/{id}/assignments shape:
 *   {item: {id, title, form_version}, counts: {pending, signed}, rows: [...], questions: [hr questions],
 *    page, per_page, total, has_more}
 * Kept in this component's state (one page, one screen); nothing else reads it.
 */

export const NO_ACCESS = "You do not have access to the documents for this announcement";
export const NOT_AVAILABLE = "This announcement is not available to you";
export const NO_ROWS = "No documents match these filters";
export const STATUS_LABEL = { pending: "Pending", signed: "Signed" };
const STATUSES = [["all", "All"], ["pending", "Pending"], ["signed", "Signed"]];

class BroadcastDocuments extends Component {

  state = {
    loaded: false,
    data: null,
    error: null,          // {status, messages} of the last failed load
    status: "all",        // what the select shows
    search: "",           // what the search box shows
    applied: { status: "all", search: "" },
    page: 1,
    raising: false,
  };

  itemId = () => this.props.params.id;

  componentDidMount() {
    this.load(1).then(() => this.setState({ loaded: true }));
  }

  query = (applied, page) => {
    const q = { status: applied.status, page: page };
    if (String(applied.search || "").trim() !== "") q.search = String(applied.search).trim();
    return q;
  };

  load = (page, applied = this.state.applied) => {
    return this.props.fetchBroadcastAssignments(this.itemId(), this.query(applied, page))
      .then((data) => this.setState({ data: data, error: null, page: Number(data && data.page) || page }))
      .catch((error) => this.setState({ data: null, error: error || {} }));
  };

  onApply = () => {
    const applied = { status: this.state.status, search: this.state.search };
    this.setState({ applied: applied });
    return this.load(1, applied);
  };

  onWithdraw = (row) => {
    if (!window.confirm("Withdraw the document for " + row.full_name + "? They will no longer be asked to sign it.")) return;
    this.props.withdrawBroadcastAssignment(this.itemId(), row.id).then((ok) => {
      if (ok) this.load(this.state.page);
    });
  };

  onExport = () => this.props.exportBroadcastAssignments(this.itemId(), this.state.applied.status, "xlsx");

  onPdf = (row) => {
    const url = row.pdf_url || "/broadcasts/" + this.itemId() + "/assignments/" + row.id + "/pdf";
    this.props.downloadBroadcastPdf(url, "document-" + row.emp_num + "-" + row.id + ".pdf");
  };

  onSaved = () => {
    this.setState({ raising: false });
    this.load(1);
  };

  renderRow = (row) => (
    <tr key={row.id}>
      <td>
        <div>{row.emp_num} — {row.full_name}</div>
        <small className="text-muted">{[row.department_name, row.country_name].filter(Boolean).join(", ")}</small>
      </td>
      <td>{STATUS_LABEL[row.status] || row.status}</td>
      <td>{row.raised_by_name}</td>
      <td className="broadcast-window">{row.raised_at}</td>
      <td className="broadcast-window">{row.signed_at || ""}</td>
      <td className="broadcast-actions">
        <Button size="sm" variant="outline-secondary" onClick={() => this.onPdf(row)}>PDF</Button>
        {row.status === "pending" ? (
          <Button size="sm" variant="outline-danger" onClick={() => this.onWithdraw(row)}>Withdraw</Button>
        ) : null}
      </td>
    </tr>
  );

  renderReport = (data) => {
    const counts = data.counts || {};
    const rows = data.rows || [];
    const page = Number(data.page || 1);
    return (
      <>
        <h5 className="broadcast-responses-title">{data.item ? data.item.title : ""}</h5>
        <div className="broadcast-metrics">
          <Metric label="Pending" value={Number(counts.pending || 0)} />
          <Metric label="Signed" value={Number(counts.signed || 0)} />
        </div>
        <div className="form-row broadcast-filters">
          <div className="col-md-2">
            <Select id="documents-status" label="Status" value={this.state.status} onChange={(v) => this.setState({ status: v })} options={STATUSES} />
          </div>
          <div className="col-md-4">
            <Text id="documents-search" label="Search" value={this.state.search} placeholder="Employee number or name"
              onChange={(v) => this.setState({ search: v })} />
          </div>
          <div className="col-md-2 broadcast-filters-apply">
            <Button variant="outline-primary" type="button" onClick={this.onApply}>Apply filters</Button>
          </div>
        </div>
        <div className="broadcast-toolbar">
          <div className="btn-group" role="group">
            <Button variant="outline-secondary" type="button" disabled={page <= 1} onClick={() => this.load(page - 1)}>Previous</Button>
            <Button variant="outline-secondary" type="button" disabled={!data.has_more} onClick={() => this.load(page + 1)}>Next</Button>
          </div>
          <div>
            <Button variant="success" type="button" className="mr-1" onClick={() => this.setState({ raising: true })}>Raise document</Button>
            <Button variant="outline-success" type="button" onClick={this.onExport}>Export (Excel)</Button>
          </div>
        </div>
        {rows.length ? (
          <Table responsive hover size="sm" className="broadcast-table broadcast-documents-table">
            <thead>
              <tr><th>Employee</th><th>Status</th><th>Raised by</th><th>Raised at</th><th>Signed at</th><th></th></tr>
            </thead>
            <tbody>{rows.map(this.renderRow)}</tbody>
          </Table>
        ) : <p>{NO_ROWS}</p>}
        {this.state.raising ? (
          <RaiseDocumentModal
            formVersion={data.item ? data.item.form_version : undefined}
            questions={data.questions || []}
            searchEmployees={this.props.searchBroadcastEmployees}
            raise={(body) => this.props.raiseBroadcastAssignment(this.itemId(), body)}
            onClose={() => this.setState({ raising: false })}
            onSaved={this.onSaved}
          />
        ) : null}
      </>
    );
  };

  renderBody = () => {
    if (!this.state.loaded) return <PageLoading />;
    if (this.state.data) return this.renderReport(this.state.data);
    const error = this.state.error || {};
    if (error.status === 403) return <p>{NO_ACCESS}</p>;
    if (error.status === 404) return <p>{NOT_AVAILABLE}</p>;
    if (error.status === 422) return <p>{(error.messages || [])[0]}</p>;
    return <p>Couldn't load the documents right now.</p>;
  };

  render() {
    return <Wrapper {...this.props} >
      <ContainerWrapper>
        <Content col="12" title="Announcement documents">
          <p><Link to={global.links.broadcast_manage}>Back to list</Link></p>
          {this.renderBody()}
        </Content>
      </ContainerWrapper>
    </Wrapper>;
  }
}

const mapStateToProps = (state) => {
  return {
    user : state.user,
  }
}
const mapDispatchToProps = (dispatch) => {
  return {
    fetchBroadcastAssignments    : ( id, query ) => dispatch( fetchBroadcastAssignments( id, query ) ),
    raiseBroadcastAssignment     : ( id, body ) => dispatch( raiseBroadcastAssignment( id, body ) ),
    withdrawBroadcastAssignment  : ( id, aid ) => dispatch( withdrawBroadcastAssignment( id, aid ) ),
    exportBroadcastAssignments   : ( id, status, format ) => dispatch( exportBroadcastAssignments( id, status, format ) ),
    downloadBroadcastPdf         : ( url, name ) => dispatch( downloadBroadcastPdf( url, name ) ),
    searchBroadcastEmployees     : ( search ) => dispatch( searchBroadcastEmployees( search ) ),
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(BroadcastDocuments);
